import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import Swal from 'sweetalert2';

import { useCheckDuplicateFileName } from './useCheckDupliactedFileName';
import usePostCompleteFileUpload from './usePostCompleteFileUpload';
import usePutManualPresignedUrl from './usePutManualPresignedUrl';
import type { IDuplicateFileNameCheckParams } from '../../../../../../../shared/apis/prodoq/types';
import { useParams } from 'react-router-dom';
import { queries } from '../../../../../../../shared/queries';

interface UseManualFileUploadParams {
  onClose: () => void;
}

export default function useManualFileUpload(params: UseManualFileUploadParams) {
  const { onClose } = params;
  const { companyId } = useParams();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const duplicateFileNameParams = {
    fileName: file?.name || '',
  };

  const { mutateAsync: putPresignedUrl } = usePutManualPresignedUrl();
  const { mutateAsync: postUploadCompleted } = usePostCompleteFileUpload();
  const { refetch: checkDuplicateFileName } = useCheckDuplicateFileName({
    companyId: Number(companyId),
    fileName: file?.name || '',
  });

  const resetStates = () => {
    setFile(null);
    setIsLoading(false);
  };

  const checkDuplicateFile = async (
    data: IDuplicateFileNameCheckParams,
  ): Promise<'duplicate-confirmed' | 'duplicate-canceled' | 'ok'> => {
    if (!data.fileName) return 'ok';

    const { data: result } = await checkDuplicateFileName();

    if (result === 'Y') {
      const confirmResult = await Swal.fire({
        icon: 'warning',
        html: `<p>동일한 파일명이 존재합니다. <br/> 업로드 파일을 다시 확인해주세요.</p>`,
        confirmButtonText: '무시하고 업로드',
        showCancelButton: true,
        cancelButtonText: '닫기',
      });

      if (confirmResult.isConfirmed) {
        return 'duplicate-confirmed';
      } else {
        return 'duplicate-canceled';
      }
    }

    return 'ok';
  };

  const uploadToPresignedUrl = async (presignedUrl: string, fileUploadId: number, contentType: string) => {
    if (!file) return;

    setIsLoading(true);

    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType || 'application/pdf' },
    });

    if (!response.ok) {
      throw new Error('file upload failed');
    }

    await postUploadCompleted({
      fileUploadId,
      fileSize: file.size,
    });

    queryClient.invalidateQueries({ queryKey: queries.aiAgent.aiAgentManuals._def });
    resetStates();
    onClose();
  };

  const onClickUpload = async () => {
    if (!file) {
      await Swal.fire({
        icon: 'warning',
        title: '파일을 선택해주세요.',
        confirmButtonText: '확인',
      });
      return;
    }

    const duplicateStatus = await checkDuplicateFile(duplicateFileNameParams);

    if (duplicateStatus === 'duplicate-canceled') {
      return;
    }

    try {
      const result = await putPresignedUrl({
        companyId: Number(companyId),
        data: {
          fileName: file.name,
        },
      });
      const presignedData = result.data;

      if (!presignedData.presignedUrl) {
        throw new Error('Presigned URL not available');
      }

      await uploadToPresignedUrl(presignedData.presignedUrl, presignedData.fileUploadId, presignedData.contentType);
    } catch (error) {
      setIsLoading(false);
      await Swal.fire({
        icon: 'error',
        title: '파일 업로드 실패',
        text: '일시적인 오류로 파일 업로드에 실패했습니다.\n다시 시도해주세요.',
        confirmButtonText: '확인',
      });
    }
  };

  return {
    file,
    isLoading,
    onClickUpload,
    setFile,
    resetStates,
  };
}
